import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    CheckboxFormInput,
    TextAreaFormInput,
    TextFormInput,
} from "src/components/form";
import { TrialWebsiteDetailsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import { RegistrationFormComponent } from "src/interfaces/forms/IRegistrationComponent";
import { ITrialWebsiteReduxFormState } from "src/reducers/reactReduxForms/trialWebsite";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialWebsiteActions from "../../../actions/trialWebsite";
import { NotificationAlert } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { TrialSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./TrialWebsitePage.scss";

interface TrialWebsitePageProps {
    form: Dtos.TrialWebsite & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ITrialWebsiteReduxFormState;
    trialWebsite: Dtos.TrialWebsite;
    lookups: Dtos.Lookup[];
    loadingTrialWebsite: boolean;
    loadTrialWebsiteSuccess: boolean;
    loadTrialWebsiteFailure: boolean;
    loadTrialWebsiteErrorCode: string;
    savingTrialWebsite: boolean;
    saveTrialWebsiteSuccess: boolean;
    saveTrialWebsiteFailure: boolean;
    validatingTrialWebsite: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialWebsite: TrialWebsiteActions.ITrialWebsiteClearActionCreator;
    loadTrialWebsite: TrialWebsiteActions.ITrialWebsiteLoadByTrialIdActionCreator;
    saveTrialWebsite: TrialWebsiteActions.ITrialWebsiteSaveActionCreator;
    validateTrialWebsite: TrialWebsiteActions.ITrialWebsiteValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trialWebsite";

const TrialWebsitePage: FC<TrialWebsitePageProps> = ({
    form,
    formState,
    formProperties,
    trialWebsite,
    lookups,
    loadingTrialWebsite,
    loadTrialWebsiteFailure,
    loadTrialWebsiteErrorCode,
    savingTrialWebsite,
    saveTrialWebsiteSuccess,
    saveTrialWebsiteFailure,
    validatingTrialWebsite,
    validationFailures,
    permissions,
    clearTrialWebsite,
    loadTrialWebsite,
    saveTrialWebsite,
    validateTrialWebsite,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const trialIdNumber = parseInt(trialId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Trial Website
    useEffect(() => {
        if (trialIdNumber && !trialWebsite) {
            setBreadcrumbs(TrialWebsiteDetailsBreadcrumbs(trialIdNumber));
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));

            loadTrialWebsite(trialIdNumber);
        } else if (trialWebsite && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        trialIdNumber,
        trialWebsite,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadTrialWebsite,
        TrialWebsiteDetailsBreadcrumbs,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialWebsiteFailure) {
            navigate(`/error/${loadTrialWebsiteErrorCode}`);
        }
    }, [loadTrialWebsiteFailure, loadTrialWebsiteErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialIdNumber && trialWebsite?.trial) {
            setBreadcrumbs(
                TrialWebsiteDetailsBreadcrumbs(
                    trialIdNumber,
                    trialWebsite.trial?.displayName
                )
            );
        }
    }, [
        trialIdNumber,
        trialWebsite,
        setBreadcrumbs,
        TrialWebsiteDetailsBreadcrumbs,
    ]);

    // Reload trial website after saving
    useEffect(() => {
        if (!savingTrialWebsite && saveTrialWebsiteSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial website saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                const url = `/trials`;
                navigate(url);
            } else if (!trialIdNumber) {
                const url = `/trials/${trialIdNumber}/website`;
                navigate(url);
                clearTrialWebsite();
            }
        }
    }, [
        savingTrialWebsite,
        saveTrialWebsiteSuccess,
        trialIdNumber,
        navigate,
        saveAndReturn,
        clearTrialWebsite,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialWebsite && saveTrialWebsiteFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialWebsite, saveTrialWebsiteSuccess]);

    // Cleanup effect to clear the trial website on component unmount
    useEffect(() => {
        return () => {
            clearTrialWebsite();
            resetForm(reduxFormName);
        };
    }, [clearTrialWebsite, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialWebsite = {} as Dtos.TrialWebsite;

            if (!data.trialId) {
                changes.trialId = trialIdNumber;
            }

            await saveTrialWebsite(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialWebsite) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const url = "/trials";
        navigate(url);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.TrialWebsite = {} as Dtos.TrialWebsite;

        loadForm(reduxFormName, Object.assign({ ...trialWebsite }, extraData));
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialWebsite = {} as Dtos.TrialWebsite;

            if (form && hasBeenSubmitted && !validatingTrialWebsite) {
                await validateTrialWebsite(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                trialIdNumber,
                null,
                permissions,
                Dtos.Permission.TrialWebsiteUpdate,
                Dtos.Permission.TrialWebsiteCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialWebsite ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialWebsite",
                                        {
                                            shortDescription: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    rows: 3,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialWebsite",
                                        {
                                            registered: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    rows: 3,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialWebsite",
                                        {
                                            enquiryEmail: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialWebsite",
                                        {
                                            phoneNumber: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="offset-xl-2 col-sm-2 col-xl-1">
                                    {renderFormControls(
                                        form,
                                        "trialWebsite",
                                        {
                                            live: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Live",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label d-none",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-7">
                                    {renderFormControls(
                                        form,
                                        "trialWebsite",
                                        {
                                            hidePublic: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Hide Public",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label d-none",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "trialWebsite",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        permissions,
        savingTrialWebsite,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        trialWebsite,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={loadingTrialWebsite || savingTrialWebsite}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialWebsitePageProps> => {
    let trialWebsite: Dtos.TrialWebsite | undefined = !(
        state.trialWebsite.formData instanceof Array
    )
        ? state.trialWebsite.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialWebsite: trialWebsite!,
        loadingTrialWebsite:
            state.trialWebsite.loadState &&
            state.trialWebsite.loadState.status == RequestState.Pending,
        loadTrialWebsiteSuccess:
            state.trialWebsite.loadState &&
            state.trialWebsite.loadState.status == RequestState.Success,
        loadTrialWebsiteFailure:
            state.trialWebsite.loadState &&
            state.trialWebsite.loadState.status == RequestState.Failure,
        loadTrialWebsiteErrorCode: state.trialWebsite.loadState.errorCode,
        savingTrialWebsite:
            state.trialWebsite.saveState &&
            state.trialWebsite.saveState.status == RequestState.Pending,
        saveTrialWebsiteSuccess:
            state.trialWebsite.saveState &&
            state.trialWebsite.saveState.status == RequestState.Success,
        saveTrialWebsiteFailure:
            state.trialWebsite.saveState &&
            state.trialWebsite.saveState.status == RequestState.Failure,
        validatingTrialWebsite:
            state.trialWebsite.validateState &&
            state.trialWebsite.validateState.status == RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.trialWebsite.lookups,
        form: state.reduxForms.trialWebsite,
        formState: state.trialWebsite.formState,
        formProperties: state.trialWebsite.formProperties,
        reduxFormState: state.reduxForms.formState.trialWebsite,
        validationFailures: trialWebsite
            ? state.trialWebsite.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialWebsitePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialWebsite: bindActionCreators(
            TrialWebsiteActions.LoadTrialWebsiteByTrialId,
            dispatch
        ),
        clearTrialWebsite: bindActionCreators(
            TrialWebsiteActions.Clear,
            dispatch
        ),
        saveTrialWebsite: bindActionCreators(
            TrialWebsiteActions.SaveTrialWebsite,
            dispatch
        ),
        validateTrialWebsite: bindActionCreators(
            TrialWebsiteActions.ValidateTrialWebsite,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialWebsitePage);
